class CustomerService extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;

  /** @type {HTMLAnchorElement[]} */
  #links = [];

  /** @type {HTMLElement[]} */
  #sections = [];

  /** @type {HTMLElement | Window} */
  #scrollContainer = window;

  /** @type {IntersectionObserver | undefined} */
  #sectionObserver;

  connectedCallback() {
    this.#controller?.abort();
    this.#sectionObserver?.disconnect();
    this.#controller = new AbortController();

    const { signal } = this.#controller;
    this.#links = Array.from(this.querySelectorAll('[data-customer-service-nav-link]')).filter(
      (link) => link instanceof HTMLAnchorElement
    );
    this.#sections = Array.from(this.querySelectorAll('[data-customer-service-section]')).filter(
      (section) => section instanceof HTMLElement
    );
    const pageWrapper = this.closest('.page-wrapper');
    const pageWrapperStyles = pageWrapper instanceof HTMLElement ? window.getComputedStyle(pageWrapper) : null;
    const pageWrapperIsScrollable =
      pageWrapper instanceof HTMLElement &&
      pageWrapper.scrollHeight > pageWrapper.clientHeight &&
      ['auto', 'scroll', 'overlay'].includes(pageWrapperStyles?.overflowY ?? '');
    this.#scrollContainer = pageWrapperIsScrollable ? pageWrapper : window;

    this.#links.forEach((link) => {
      link.addEventListener('click', (event) => this.#scrollToSection(event, link), { signal });
    });

    this.#scrollContainer.addEventListener('scroll', () => this.#syncActiveLink(), { passive: true, signal });
    window.addEventListener('resize', () => this.#syncActiveLink(), { passive: true, signal });
    window.addEventListener('pageshow', () => this.#syncActiveLink(), { signal });

    if ('IntersectionObserver' in window) {
      this.#sectionObserver = new IntersectionObserver(() => this.#syncActiveLink(), {
        root: this.#scrollContainer instanceof HTMLElement ? this.#scrollContainer : null,
        rootMargin: '-160px 0px -60% 0px',
        threshold: [0, 1],
      });
      this.#sections.forEach((section) => this.#sectionObserver?.observe(section));
    }

    this.#syncActiveLink();

    this.querySelectorAll('[data-customer-service-faq-button]').forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;
      button.addEventListener('click', () => this.#toggleFaq(button), { signal });
    });

    this.querySelectorAll('[data-customer-service-video-button]').forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;
      button.addEventListener('click', () => this.#loadVideo(button), { signal });
    });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#sectionObserver?.disconnect();
  }

  /**
   * @param {MouseEvent} event
   * @param {HTMLAnchorElement} link
   */
  #scrollToSection(event, link) {
    const targetId = link.hash.slice(1);
    const target = targetId ? this.querySelector(`#${CSS.escape(targetId)}`) : null;
    if (!(target instanceof HTMLElement)) return;

    event.preventDefault();
    this.#setActiveLink(link);
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    target.scrollIntoView({ behavior, block: 'start' });
  }

  #syncActiveLink() {
    if (this.#sections.length === 0) return;

    let activeSection = this.#sections[0];
    const isElementScroller = this.#scrollContainer instanceof HTMLElement;
    const scrollTop = isElementScroller ? this.#scrollContainer.scrollTop : window.scrollY;
    const viewportHeight = isElementScroller ? this.#scrollContainer.clientHeight : window.innerHeight;
    const scrollHeight = isElementScroller
      ? this.#scrollContainer.scrollHeight
      : document.documentElement.scrollHeight;
    const activationTop = isElementScroller ? this.#scrollContainer.getBoundingClientRect().top + 160 : 160;
    const nearBottom = viewportHeight + scrollTop >= scrollHeight - 4;

    if (nearBottom) {
      activeSection = this.#sections[this.#sections.length - 1];
    } else {
      this.#sections.forEach((section) => {
        if (section.getBoundingClientRect().top <= activationTop) activeSection = section;
      });
    }

    const activeLink = this.#links.find((link) => link.hash === `#${activeSection.id}`);
    if (activeLink) this.#setActiveLink(activeLink);
  }

  /** @param {HTMLAnchorElement} activeLink */
  #setActiveLink(activeLink) {
    const activeLinkChanged = activeLink.getAttribute('aria-current') !== 'true';

    this.#links.forEach((link) => {
      link.setAttribute('aria-current', String(link === activeLink));
    });

    if (activeLinkChanged) this.#keepActiveLinkVisible(activeLink);
  }

  /** @param {HTMLAnchorElement} activeLink */
  #keepActiveLinkVisible(activeLink) {
    if (!window.matchMedia('(max-width: 900px)').matches) return;

    const navigation = activeLink.closest('.customer-service__navigation');
    if (!(navigation instanceof HTMLElement) || navigation.scrollWidth <= navigation.clientWidth) return;

    const navigationRect = navigation.getBoundingClientRect();
    const activeLinkRect = activeLink.getBoundingClientRect();
    let nextScrollLeft = navigation.scrollLeft;

    if (activeLinkRect.left < navigationRect.left) {
      nextScrollLeft -= navigationRect.left - activeLinkRect.left;
    } else if (activeLinkRect.right > navigationRect.right) {
      nextScrollLeft += activeLinkRect.right - navigationRect.right;
    } else {
      return;
    }

    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    navigation.scrollTo({ left: nextScrollLeft, behavior });
  }

  /** @param {HTMLButtonElement} button */
  #loadVideo(button) {
    const videoId = button.dataset.videoId;
    const videoType = button.dataset.videoType;
    if (!videoId || !['youtube', 'vimeo'].includes(videoType)) return;

    const iframe = document.createElement('iframe');
    const videoTitle = button.getAttribute('aria-label') || '';
    const encodedVideoId = encodeURIComponent(videoId);

    iframe.className = 'customer-service__video-frame';
    iframe.title = videoTitle;
    iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
    iframe.allowFullscreen = true;
    iframe.referrerPolicy = 'strict-origin-when-cross-origin';
    iframe.src =
      videoType === 'youtube'
        ? `https://www.youtube-nocookie.com/embed/${encodedVideoId}?autoplay=1&rel=0`
        : `https://player.vimeo.com/video/${encodedVideoId}?autoplay=1`;

    button.replaceWith(iframe);
  }

  /** @param {HTMLButtonElement} button */
  #toggleFaq(button) {
    const isOpen = button.getAttribute('aria-expanded') === 'true';

    if (!isOpen && this.dataset.allowMultiple !== 'true') {
      const panel = button.closest('[data-customer-service-section]');
      panel?.querySelectorAll('[data-customer-service-faq-button][aria-expanded="true"]').forEach((openButton) => {
        if (openButton instanceof HTMLButtonElement) this.#setFaqState(openButton, false);
      });
    }

    this.#setFaqState(button, !isOpen);
  }

  /**
   * @param {HTMLButtonElement} button
   * @param {boolean} open
   */
  #setFaqState(button, open) {
    const answerId = button.getAttribute('aria-controls');
    const answer = answerId ? this.querySelector(`#${CSS.escape(answerId)}`) : null;

    button.setAttribute('aria-expanded', String(open));
    answer?.toggleAttribute('inert', !open);
    answer?.setAttribute('aria-hidden', String(!open));
  }
}

if (!customElements.get('customer-service')) {
  customElements.define('customer-service', CustomerService);
}
